import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, gbp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StageBadge } from "@/components/tam/ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ContactForm } from "@/components/tam/ContactForm";
import { ActivityPanel } from "@/components/tam/ActivityPanel";
import { NotesPanel } from "@/components/tam/NotesPanel";
import { TradingHistory } from "@/components/tam/TradingHistory";

export default function AccountDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [acc, setAcc] = useState(null);
  const load = useCallback(() => api.get(`/accounts/${id}`).then((r) => setAcc(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!acc) return <p className="text-slate-500">Loading account…</p>;
  const canEdit = user.role !== "purfleet_manager";

  return (
    <div data-testid="account-detail-page">
      <Link to="/accounts" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#D9232A] mb-4" data-testid="back-to-accounts"><ArrowLeft size={14} /> Accounts</Link>
      <PageHeader title={acc.name} subtitle={`Acc ${acc.acc_no} · ${acc.owner} · Rep ${acc.rep} · ${acc.price_band}`}>
        <div className="text-right">
          <StageBadge stage={acc.stage} testId="account-stage-badge" />
          <p className="font-display text-2xl font-bold text-[#0B132B] mt-2" data-testid="account-total-value">{gbp(acc.total_value)}</p>
          <p className="text-xs text-slate-500">Last traded {acc.last_trading_year || "—"}</p>
        </div>
      </PageHeader>
      <Tabs defaultValue="work">
        <TabsList className="mb-5">
          <TabsTrigger value="work" data-testid="tab-work">Contact &amp; Activity</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Trading History</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
        </TabsList>
        <TabsContent value="work">
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5"><ContactForm account={acc} canEdit={canEdit} onSaved={load} /></div>
            <div className="lg:col-span-7"><ActivityPanel account={acc} canEdit={canEdit} onLogged={load} /></div>
          </div>
        </TabsContent>
        <TabsContent value="history"><TradingHistory account={acc} /></TabsContent>
        <TabsContent value="notes"><NotesPanel accountId={acc.id} /></TabsContent>
      </Tabs>
    </div>
  );
}
